import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import SearchPending from "../../components/SearchPending";
import { CheckCircle2 } from "lucide-react";
import { candidateName } from "../../utils/candidateName";
import { createListSearch } from "../../utils/listSearch";
import React, { useState, useEffect } from "react";
import axiosInstance from "../../axios/AxiosInstance";
import { Input, Modal } from "antd";

const CreateContractCandidate = ({ contract, setContract }) => {
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [query, setQuery] = useState("");
  const [appliedSearch, searchPending] = useDebouncedValue(query);

  const selectedCandidates = appliedSearch.trim()
    ? candidates.filter(
        createListSearch(appliedSearch, [
          "firstname",
          "lastname",
          "phone",
          "email",
          "town",
          "postCode",
          "qualifications",
          "pld",
        ]),
      )
    : [];
  useEffect(() => {
    let active = true;
    axiosInstance
      .get("tempworker-candidate/freelance")
      .then(({ data }) => {
        if (active) setCandidates(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (active) setSearchError("Impossible de charger les candidats. Réouvrez la fenêtre pour réessayer."); })
      .finally(() => { if (active) setLoadingCandidates(false); });
    return () => {
      active = false;
    };
  }, []);
  const [isMissingModal, setIsMissingModal] = useState(false);
  const [missingParts, setMissingParts] = useState([]);

  const selectCandidate = (candidate) => {
    setQuery("");
    setIsMissingModal(false);
    setMissingParts([]);
    const newMissingParts = [];
    if (!candidate.title) {
      newMissingParts.push("Civilité");
    }
    if (!candidate.firstname) {
      newMissingParts.push("Prénom");
    }
    if (!candidate.lastname) {
      newMissingParts.push("Nom");
    }
    if (!candidate.dob) {
      newMissingParts.push("Date de naissance");
    }
    if (!candidate.email) {
      newMissingParts.push("Email");
    }
    if (!candidate.phone) {
      newMissingParts.push("Téléphone");
    }
    if (!candidate.birthTown) {
      newMissingParts.push("Ville de naissance");
    }
    if (!candidate.birthDepartment) {
      newMissingParts.push("Département de naissance");
    }
    if (!candidate.birthCountry) {
      newMissingParts.push("Pays de naissance");
    }
    if (!candidate.securityNumber) {
      newMissingParts.push("Numero de sécurité sociale");
    }
    if (!candidate.nationality) {
      newMissingParts.push("Nationalité");
    }
    if (!candidate.address) {
      newMissingParts.push("Adresse");
    }
    if (!candidate.postCode) {
      newMissingParts.push("Code postal");
    }
    if (!candidate.town) {
      newMissingParts.push("Ville");
    }

    setContract(current => ({ ...current, candidate }));
    axiosInstance
      .get(`document/id/${candidate._id}`)
      .then((res) => {
        if (!res.data.length) {
          newMissingParts.push("Document d'identité");
        }
        if (newMissingParts.length && !candidate.pld) {
          setMissingParts(newMissingParts);
          setIsMissingModal(true);
        }
      })
      .catch(() => {});
  };
  const search = setQuery;

  return (
    <div className="w-full space-y-2">
      {isMissingModal && (
        <Modal
          open={isMissingModal}
          footer={null}
          zIndex={2000}
          onCancel={() => setIsMissingModal(false)}
        >
          <div className="h-[400px]">
            <p className="font-semibold text-2xl">Informations manquantes</p>
            {missingParts.map((part) => {
              return <p key={part} className="text-red-600 mt-4 font-semibold">{part}</p>;
            })}
          </div>
        </Modal>
      )}
      <p className="text-sm font-semibold text-slate-900">Candidat</p>
      <p className="text-xs text-slate-500">
        La fiche du candidat doit être complète pour créer le contrat.
      </p>
      <Input
        className="text-sm text-slate-900 rounded-lg placeholder-slate-400"
        placeholder="Rechercher un candidat"
        aria-label="Rechercher un candidat"
        value={query}
        onChange={(e) => search(e.target.value)}
      /><SearchPending pending={searchPending || loadingCandidates} />
      {searchError && <p role="alert" className="text-sm text-rose-600">{searchError}</p>}
      {selectedCandidates.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg max-h-48 overflow-y-auto">
          {selectedCandidates.map((candidate, idx) => {
            return (
              <button
                type="button"
                key={candidate._id || idx}
                aria-pressed={contract.candidate?._id === candidate._id}
                onClick={() => selectCandidate(candidate)}
                className={`w-full cursor-pointer border px-3 py-2 text-left rounded-lg border-slate-200 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${
                  contract.candidate?._id === candidate._id
                    ? "bg-blue-50 border-blue-200"
                    : ""
                }`}
              >
                <div className="flex items-start space-x-3 justify-between mx-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {candidateName(candidate)}
                    </p>
                    <p className="text-xs text-slate-500">{candidate.email}</p>
                    <p className="text-xs text-slate-500">{candidate.phone}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {contract.candidate && selectedCandidates.length === 0 && (
        <div
          className={`border px-3 py-2 rounded-lg border-slate-200 bg-blue-50`}
        >
          <p role="status" className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-700"><CheckCircle2 size={16} aria-hidden="true"/>Candidat sélectionné</p>
          <p className="text-sm font-semibold text-slate-900">
            {candidateName(contract.candidate)}
          </p>
          <p className="text-xs text-slate-500">{contract.candidate.email}</p>
          <p className="text-xs text-slate-500">{contract.candidate.phone}</p>
        </div>
      )}
    </div>
  );
};

export default CreateContractCandidate;
